<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'

import BetaWhatsappOptionCard from '@/components/beta-dashboard/molecules/BetaWhatsappOptionCard.vue'
import countries from '@/data/countries.json'
import whatsOptions from '@/data/whatsOptions.json'
import { useApplicationStore } from '@/stores/application'
import { useSiteStore } from '@/stores/site'

const applicationStore = useApplicationStore()
const siteStore = useSiteStore()

const saving = ref(false)

const contact = reactive({
    userEmail: '',
    mainEmail: '',
    contactPhone: '',
    address: '',
    idd: '' as string | number,
    whatsappButton: '',
    whatsappMessage: '',
    whatsappIndex: 0,
})

const showWhatsappOptions = computed(() => applicationStore.className !== 'epics')
const isBrazil = computed(() => String(contact.idd) === '30')

const optionDescription = (option: typeof whatsOptions[number]) => {
    if (option.value === 0) return 'Não exibir o botão no site'
    if (option.value === 6) return 'Definir botão e mensagem manualmente'
    return option.message
}

watch(
    [
        () => siteStore.contactEmail,
        () => siteStore.mainEmail,
        () => siteStore.contactPhone,
        () => siteStore.address,
        () => siteStore.phoneCountry,
        () => siteStore.whatsappTitle,
        () => siteStore.whatsappMessage,
        () => siteStore.whatsappIndex,
        () => applicationStore.appDefaultIdd,
    ],
    () => {
        contact.userEmail = String(siteStore.contactEmail || '')
        contact.mainEmail = String(siteStore.mainEmail || '')
        contact.contactPhone = String(siteStore.contactPhone || '')
        contact.address = String(siteStore.address || '')
        contact.idd = siteStore.phoneCountry ?? applicationStore.appDefaultIdd ?? ''
        contact.whatsappButton = String(siteStore.whatsappTitle || '')
        contact.whatsappMessage = String(siteStore.whatsappMessage || '')
        contact.whatsappIndex = Number(siteStore.whatsappIndex || 0)
    },
    { immediate: true },
)

const selectWhatsappOption = (option: typeof whatsOptions[number]) => {
    contact.whatsappIndex = option.value
    contact.whatsappButton = option.text
    contact.whatsappMessage = option.message
}

const saveContact = async () => {
    if (saving.value) return
    saving.value = true

    try {
        await siteStore.handleUpdateContact({
            ...contact,
            phoneCountry: contact.idd,
            location: siteStore.location,
        })
    } finally {
        saving.value = false
    }
}
</script>

<template>
    <form class="beta-contact" @submit.prevent="saveContact">
        <div class="beta-contact__heading">
            <div>
                <p>Contato</p>
                <h2>Onde seus clientes encontram você</h2>
            </div>
            <span>Os campos vazios continuam opcionais.</span>
        </div>

        <section class="beta-contact__section" aria-labelledby="beta-contact-channels-title">
            <div class="beta-contact__section-heading">
                <div>
                    <h3 id="beta-contact-channels-title">Canais de atendimento</h3>
                    <p>Informe os endereços usados para contato e solicitação de orçamento.</p>
                </div>
            </div>

            <div class="beta-contact__fields beta-contact__fields--two">
                <label>
                    <span>E-mail de contato</span>
                    <input
                        id="beta-contact-email"
                        v-model="contact.userEmail"
                        type="email"
                        autocomplete="email"
                    >
                    <small>Exibido para contatos gerais.</small>
                </label>
                <label>
                    <span>E-mail de orçamento</span>
                    <input
                        id="beta-budget-email"
                        v-model="contact.mainEmail"
                        type="email"
                        autocomplete="email"
                    >
                    <small>Usado nos pedidos de orçamento.</small>
                </label>
            </div>
        </section>

        <section class="beta-contact__section" aria-labelledby="beta-contact-location-title">
            <div class="beta-contact__section-heading">
                <div>
                    <h3 id="beta-contact-location-title">Telefone e endereço</h3>
                    <p>Selecione o país para aplicar o formato adequado ao telefone.</p>
                </div>
            </div>

            <div class="beta-contact__phone-row">
                <label>
                    <span>DDI</span>
                    <select id="beta-contact-idd" v-model="contact.idd">
                        <option
                            v-for="country in countries"
                            :key="country.id"
                            :value="country.id"
                        >
                            {{ country.emoji }} +{{ country.phonecode }} · {{ country.iso }}
                        </option>
                    </select>
                </label>
                <label>
                    <span>Telefone <small>DDD + número</small></span>
                    <input
                        id="beta-contact-phone"
                        v-model="contact.contactPhone"
                        v-maska
                        type="tel"
                        inputmode="tel"
                        autocomplete="tel"
                        :data-maska="isBrazil ? '(##) #####-####' : '############'"
                    >
                </label>
            </div>

            <div class="beta-contact__fields">
                <label>
                    <span>Endereço</span>
                    <input
                        id="beta-contact-address"
                        v-model="contact.address"
                        type="text"
                        autocomplete="street-address"
                        placeholder="Digite o endereço exibido no site"
                    >
                </label>
            </div>
        </section>

        <section
            v-if="showWhatsappOptions"
            class="beta-contact__section beta-contact__section--whatsapp"
            aria-labelledby="beta-contact-whatsapp-title"
        >
            <div class="beta-contact__section-heading">
                <div>
                    <h3 id="beta-contact-whatsapp-title">Botão de WhatsApp</h3>
                    <p>Escolha um texto pronto ou personalize a chamada exibida no site.</p>
                </div>
                <span>{{ contact.whatsappIndex === 0 ? 'Desativado' : 'Ativo' }}</span>
            </div>

            <div class="beta-contact__whatsapp-options">
                <BetaWhatsappOptionCard
                    v-for="option in whatsOptions"
                    :key="option.value"
                    :label="option.label"
                    :description="optionDescription(option)"
                    :active="option.value === contact.whatsappIndex"
                    @select="selectWhatsappOption(option)"
                />
            </div>

            <div v-if="contact.whatsappIndex === 6" class="beta-contact__fields beta-contact__fields--two">
                <label>
                    <span>Texto do botão</span>
                    <input
                        id="beta-whatsapp-button"
                        v-model="contact.whatsappButton"
                        type="text"
                        maxlength="80"
                    >
                </label>
                <label>
                    <span>Mensagem inicial</span>
                    <input
                        id="beta-whatsapp-message"
                        v-model="contact.whatsappMessage"
                        type="text"
                        maxlength="300"
                    >
                </label>
            </div>
        </section>

        <div class="beta-contact__actions">
            <p v-if="!showWhatsappOptions">
                As opções de WhatsApp não estão disponíveis nesta aplicação.
            </p>
            <button type="submit" :disabled="saving">
                {{ saving ? 'Salvando…' : 'Salvar contato' }}
            </button>
        </div>
    </form>
</template>

<style scoped>
.beta-contact {
    display: grid;
    gap: 18px;
}

.beta-contact__heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding-bottom: 20px;
    border-bottom: 1px solid #edf1f6;
}

.beta-contact__heading p {
    color: var(--main-color);
    font-size: 10px;
    font-weight: 900;
    letter-spacing: 0.18em;
    text-transform: uppercase;
}

.beta-contact__heading h2 {
    margin-top: 4px;
    color: #172d4a;
    font-size: 21px;
    font-weight: 850;
    letter-spacing: -0.025em;
}

.beta-contact__heading > span {
    color: #8291a7;
    font-size: 11px;
    text-align: right;
}

.beta-contact__section {
    border: 1px solid #e6ebf2;
    border-radius: 20px;
    background: #f9fbfd;
    padding: 18px;
}

.beta-contact__section--whatsapp {
    background: color-mix(in srgb, #25d366 3%, white);
}

.beta-contact__section-heading {
    display: flex;
    align-items: start;
    justify-content: space-between;
    gap: 16px;
}

.beta-contact__section-heading h3 {
    color: #233852;
    font-size: 14px;
    font-weight: 850;
}

.beta-contact__section-heading p {
    margin-top: 4px;
    color: #7e8da2;
    font-size: 12px;
}

.beta-contact__section-heading > span {
    border-radius: 999px;
    background: #e9f7ef;
    padding: 5px 8px;
    color: #28895a;
    font-size: 10px;
    font-weight: 850;
    text-transform: uppercase;
}

.beta-contact__fields,
.beta-contact__phone-row {
    margin-top: 16px;
    display: grid;
    gap: 14px;
}

.beta-contact__fields--two {
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.beta-contact__phone-row {
    grid-template-columns: minmax(150px, 0.45fr) minmax(0, 1fr);
}

.beta-contact label {
    display: grid;
    align-content: start;
    gap: 7px;
}

.beta-contact label > span {
    color: #3a506d;
    font-size: 12px;
    font-weight: 850;
}

.beta-contact label > span small,
.beta-contact label > small {
    color: #8795a7;
    font-size: 10px;
    font-weight: 500;
}

.beta-contact input,
.beta-contact select {
    width: 100%;
    min-height: 43px;
    border: 1px solid #dce4ef;
    border-radius: 14px;
    background: #fff;
    padding: 0 13px;
    color: #172d4a;
    font-size: 11px;
    outline: none;
}

.beta-contact select {
    cursor: pointer;
}

.beta-contact input:focus,
.beta-contact select:focus {
    border-color: var(--main-color);
    outline: 3px solid color-mix(in srgb, var(--main-color) 9%, transparent);
    outline-offset: 1px;
}

.beta-contact__whatsapp-options {
    margin-top: 16px;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
}

.beta-contact__actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 14px;
}

.beta-contact__actions p {
    margin-right: auto;
    color: #7f8ea2;
    font-size: 10px;
}

.beta-contact__actions button {
    min-height: 43px;
    border-radius: var(--beta-button-radius);
    background: var(--main-color);
    padding: 0 17px;
    color: #fff;
    font-size: 10px;
    font-weight: 850;
}

.beta-contact__actions button:disabled {
    cursor: wait;
    opacity: 0.55;
}

@media (max-width: 767px) {
    .beta-contact__heading {
        align-items: start;
        flex-direction: column;
    }

    .beta-contact__heading > span {
        text-align: left;
    }

    .beta-contact__fields--two,
    .beta-contact__phone-row,
    .beta-contact__whatsapp-options {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 479px) {
    .beta-contact__section {
        border-radius: 16px;
        padding: 13px;
    }

    .beta-contact__actions {
        align-items: stretch;
        flex-direction: column;
    }

    .beta-contact__actions button {
        width: 100%;
    }
}
</style>
